import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UploadCloud, X, Image, Globe, Search, Building2, Smartphone } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

interface SEOSettingsProps {
  settings: any;
  onSettingsChange: (settings: any) => void;
}

const SEOSettings = ({ settings, onSettingsChange }: SEOSettingsProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState<string>('');
  const { toast } = useToast();

  const handleIconUpload = useCallback(async (acceptedFiles: File[], iconType: 'favicon' | 'pwa' | 'apple') => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const maxSize = iconType === 'favicon' ? 1 * 1024 * 1024 : 5 * 1024 * 1024; // 1MB for favicon, 5MB for others
    
    if (file.size > maxSize) {
      toast({ 
        title: "File too large", 
        description: `Please upload an image smaller than ${iconType === 'favicon' ? '1MB' : '5MB'}.`, 
        variant: "destructive" 
      });
      return;
    }
    
    setIsUploading(true);
    setUploadingType(iconType);
    
    try {
      const fileName = `${iconType}-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(data.path);
      
      const updateKey = iconType === 'favicon' ? 'faviconUrl' : 
                       iconType === 'pwa' ? 'pwaIconUrl' : 'appleTouchIconUrl';
      
      onSettingsChange({ [updateKey]: publicUrl });
      toast({ title: "Success", description: `${iconType} icon uploaded successfully.` });

    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
      setUploadingType('');
    }
  }, [onSettingsChange, toast]);

  const createDropzone = (iconType: 'favicon' | 'pwa' | 'apple') => {
    return useDropzone({
      onDrop: (files) => handleIconUpload(files, iconType),
      accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.svg', '.ico'] },
      multiple: false,
    });
  };

  const faviconDropzone = createDropzone('favicon');
  const pwaDropzone = createDropzone('pwa');
  const appleDropzone = createDropzone('apple');

  const handleRemoveIcon = (iconType: 'favicon' | 'pwa' | 'apple') => {
    const updateKey = iconType === 'favicon' ? 'faviconUrl' : 
                     iconType === 'pwa' ? 'pwaIconUrl' : 'appleTouchIconUrl';
    onSettingsChange({ [updateKey]: '' });
    toast({ title: `${iconType} icon removed`, description: "Don't forget to save your changes." });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    if (file.size > 5 * 1024 * 1024) { // 5MB limit for OG images
      toast({ title: "File too large", description: "Please upload an image smaller than 5MB.", variant: "destructive" });
      return;
    }
    
    setIsUploading(true);
    try {
      const fileName = `og-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(data.path);
      onSettingsChange({ ogImageUrl: publicUrl });
      toast({ title: "Success", description: "OG image uploaded successfully." });

    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  }, [onSettingsChange, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    multiple: false,
  });

  const handleRemoveOGImage = () => {
    onSettingsChange({ ogImageUrl: '' });
    toast({ title: "OG image removed", description: "Don't forget to save your changes." });
  };

  return (
    <div className="space-y-6">
      {/* Favicon & App Icons */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            <CardTitle className="text-foreground">Favicon & App Icons</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground">
            Upload icons for browser tabs, mobile home screens, and PWA installations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Favicon */}
          <div className="space-y-3">
            <Label htmlFor="favicon">Website Favicon</Label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-md border flex items-center justify-center bg-muted/40">
                {settings.faviconUrl ? (
                  <img src={settings.faviconUrl} alt="Favicon" className="max-w-full max-h-full object-contain rounded" />
                ) : (
                  <Image className="w-6 h-6 text-muted-foreground"/>
                )}
              </div>
              <div className="flex-1">
                <div {...faviconDropzone.getRootProps()} className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${faviconDropzone.isDragActive ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                  <input {...faviconDropzone.getInputProps()} />
                  {isUploading && uploadingType === 'favicon' ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <p className="text-sm">Uploading favicon...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <UploadCloud className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {faviconDropzone.isDragActive ? "Drop favicon here" : "Upload favicon"}
                      </p>
                      <p className="text-xs text-muted-foreground">32x32px recommended, max 1MB</p>
                    </div>
                  )}
                </div>
                {settings.faviconUrl && (
                  <Button variant="link" size="sm" className="text-destructive mt-2" onClick={() => handleRemoveIcon('favicon')}>
                    <X className="w-4 h-4 mr-1"/> Remove Favicon
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* PWA Icon */}
          <div className="space-y-3">
            <Label htmlFor="pwaIcon">PWA App Icon</Label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-md border flex items-center justify-center bg-muted/40">
                {settings.pwaIconUrl ? (
                  <img src={settings.pwaIconUrl} alt="PWA Icon" className="max-w-full max-h-full object-contain rounded" />
                ) : (
                  <Smartphone className="w-6 h-6 text-muted-foreground"/>
                )}
              </div>
              <div className="flex-1">
                <div {...pwaDropzone.getRootProps()} className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${pwaDropzone.isDragActive ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                  <input {...pwaDropzone.getInputProps()} />
                  {isUploading && uploadingType === 'pwa' ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <p className="text-sm">Uploading PWA icon...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <UploadCloud className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {pwaDropzone.isDragActive ? "Drop PWA icon here" : "Upload PWA icon"}
                      </p>
                      <p className="text-xs text-muted-foreground">512x512px recommended for Google Play Store, max 5MB</p>
                    </div>
                  )}
                </div>
                {settings.pwaIconUrl && (
                  <Button variant="link" size="sm" className="text-destructive mt-2" onClick={() => handleRemoveIcon('pwa')}>
                    <X className="w-4 h-4 mr-1"/> Remove PWA Icon
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Apple Touch Icon */}
          <div className="space-y-3">
            <Label htmlFor="appleIcon">Apple Touch Icon</Label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-md border flex items-center justify-center bg-muted/40">
                {settings.appleTouchIconUrl ? (
                  <img src={settings.appleTouchIconUrl} alt="Apple Touch Icon" className="max-w-full max-h-full object-contain rounded" />
                ) : (
                  <Image className="w-6 h-6 text-muted-foreground"/>
                )}
              </div>
              <div className="flex-1">
                <div {...appleDropzone.getRootProps()} className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${appleDropzone.isDragActive ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                  <input {...appleDropzone.getInputProps()} />
                  {isUploading && uploadingType === 'apple' ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <p className="text-sm">Uploading Apple icon...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <UploadCloud className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {appleDropzone.isDragActive ? "Drop Apple icon here" : "Upload Apple touch icon"}
                      </p>
                      <p className="text-xs text-muted-foreground">180x180px recommended for iOS, max 5MB</p>
                    </div>
                  )}
                </div>
                {settings.appleTouchIconUrl && (
                  <Button variant="link" size="sm" className="text-destructive mt-2" onClick={() => handleRemoveIcon('apple')}>
                    <X className="w-4 h-4 mr-1"/> Remove Apple Icon
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* PWA Manifest Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Progressive Web App Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pwaName">App Name</Label>
                <Input
                  id="pwaName"
                  value={settings.pwaManifest?.name || ''}
                  onChange={(e) => onSettingsChange({ 
                    pwaManifest: { ...settings.pwaManifest, name: e.target.value } 
                  })}
                  placeholder="VRAM Store"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwaShortName">Short Name</Label>
                <Input
                  id="pwaShortName"
                  value={settings.pwaManifest?.short_name || ''}
                  onChange={(e) => onSettingsChange({ 
                    pwaManifest: { ...settings.pwaManifest, short_name: e.target.value } 
                  })}
                  placeholder="VRAM"
                  maxLength={12}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwaThemeColor">Theme Color</Label>
                <Input
                  id="pwaThemeColor"
                  type="color"
                  value={settings.pwaManifest?.theme_color || '#000000'}
                  onChange={(e) => onSettingsChange({ 
                    pwaManifest: { ...settings.pwaManifest, theme_color: e.target.value } 
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwaBackgroundColor">Background Color</Label>
                <Input
                  id="pwaBackgroundColor"
                  type="color"
                  value={settings.pwaManifest?.background_color || '#ffffff'}
                  onChange={(e) => onSettingsChange({ 
                    pwaManifest: { ...settings.pwaManifest, background_color: e.target.value } 
                  })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwaDescription">App Description</Label>
              <Textarea
                id="pwaDescription"
                value={settings.pwaManifest?.description || ''}
                onChange={(e) => onSettingsChange({ 
                  pwaManifest: { ...settings.pwaManifest, description: e.target.value } 
                })}
                placeholder="Advanced admin management system"
                rows={2}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic SEO */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            <CardTitle className="text-foreground">Search Engine Optimization</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground">
            Control how your store appears in Google search results
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="seoTitle">SEO Title</Label>
            <Input
              id="seoTitle"
              value={settings.seoTitle || ''}
              onChange={(e) => onSettingsChange({ seoTitle: e.target.value })}
              placeholder="VRAM Store - Quality Products Online"
              maxLength={60}
            />
            <p className="text-xs text-muted-foreground">
              This appears as the blue link in Google search results (50-60 characters recommended)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="seoDescription">SEO Description</Label>
            <Textarea
              id="seoDescription"
              value={settings.seoDescription || ''}
              onChange={(e) => onSettingsChange({ seoDescription: e.target.value })}
              placeholder="Discover amazing products at VRAM Store. Quality guaranteed, fast shipping, competitive prices."
              maxLength={160}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              This appears as the grey text below your title in search results (150-160 characters recommended)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="seoKeywords">SEO Keywords</Label>
            <Input
              id="seoKeywords"
              value={settings.seoKeywords || ''}
              onChange={(e) => onSettingsChange({ seoKeywords: e.target.value })}
              placeholder="online store, quality products, fast shipping"
            />
            <p className="text-xs text-muted-foreground">
              Separate keywords with commas. Focus on terms your customers search for.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="canonicalUrl">Canonical URL</Label>
            <Input
              id="canonicalUrl"
              value={settings.canonicalUrl || ''}
              onChange={(e) => onSettingsChange({ canonicalUrl: e.target.value })}
              placeholder="https://maisongroup.shop"
            />
            <p className="text-xs text-muted-foreground">
              Your main website URL. Helps prevent duplicate content issues.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Social Media & Open Graph */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            <CardTitle className="text-foreground">Social Media & Open Graph</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground">
            Control how your store appears when shared on social media
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ogImage">Social Media Preview Image</Label>
            <div className="flex items-center gap-4">
              <div className="w-32 h-24 rounded-md border flex items-center justify-center bg-muted/40">
                {settings.ogImageUrl ? (
                  <img src={settings.ogImageUrl} alt="OG preview" className="max-w-full max-h-full object-contain rounded" />
                ) : (
                  <Image className="w-10 h-10 text-muted-foreground"/>
                )}
              </div>
              <div className="flex-1">
                <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                  <input {...getInputProps()} id="ogImage" />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <p className="text-sm">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <UploadCloud className="h-6 w-6 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {isDragActive ? "Drop image here" : "Upload social preview image"}
                      </p>
                      <p className="text-xs text-muted-foreground">1200x630px recommended, max 5MB</p>
                    </div>
                  )}
                </div>
                {settings.ogImageUrl && (
                  <Button variant="link" size="sm" className="text-destructive mt-2" onClick={handleRemoveOGImage}>
                    <X className="w-4 h-4 mr-1"/> Remove Image
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="facebookUrl">Facebook Page</Label>
              <Input
                id="facebookUrl"
                value={settings.socialLinks?.facebook || ''}
                onChange={(e) => onSettingsChange({ socialLinks: { ...settings.socialLinks, facebook: e.target.value } })}
                placeholder="https://facebook.com/yourstore"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="twitterUrl">Twitter/X Profile</Label>
              <Input
                id="twitterUrl"
                value={settings.socialLinks?.twitter || ''}
                onChange={(e) => onSettingsChange({ socialLinks: { ...settings.socialLinks, twitter: e.target.value } })}
                placeholder="https://twitter.com/yourstore"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagramUrl">Instagram Profile</Label>
              <Input
                id="instagramUrl"
                value={settings.socialLinks?.instagram || ''}
                onChange={(e) => onSettingsChange({ socialLinks: { ...settings.socialLinks, instagram: e.target.value } })}
                placeholder="https://instagram.com/yourstore"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn Page</Label>
              <Input
                id="linkedinUrl"
                value={settings.socialLinks?.linkedin || ''}
                onChange={(e) => onSettingsChange({ socialLinks: { ...settings.socialLinks, linkedin: e.target.value } })}
                placeholder="https://linkedin.com/company/yourstore"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <CardTitle className="text-foreground">Business Information</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground">
            Business details for Google Business Profile and local SEO
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                value={settings.businessName || ''}
                onChange={(e) => onSettingsChange({ businessName: e.target.value })}
                placeholder="VRAM Store"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessPhone">Business Phone</Label>
              <Input
                id="businessPhone"
                value={settings.businessPhone || ''}
                onChange={(e) => onSettingsChange({ businessPhone: e.target.value })}
                placeholder="+61 3 9XXX XXXX"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="businessDescription">Business Description</Label>
            <Textarea
              id="businessDescription"
              value={settings.businessDescription || ''}
              onChange={(e) => onSettingsChange({ businessDescription: e.target.value })}
              placeholder="Your trusted online store for quality products with fast shipping across Australia"
              rows={2}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-medium">Business Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="addressStreet">Street Address</Label>
                <Input
                  id="addressStreet"
                  value={settings.businessAddress?.street || ''}
                  onChange={(e) => onSettingsChange({ 
                    businessAddress: { ...settings.businessAddress, street: e.target.value } 
                  })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressCity">City</Label>
                <Input
                  id="addressCity"
                  value={settings.businessAddress?.city || ''}
                  onChange={(e) => onSettingsChange({ 
                    businessAddress: { ...settings.businessAddress, city: e.target.value } 
                  })}
                  placeholder="Coburg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressState">State</Label>
                <Input
                  id="addressState"
                  value={settings.businessAddress?.state || ''}
                  onChange={(e) => onSettingsChange({ 
                    businessAddress: { ...settings.businessAddress, state: e.target.value } 
                  })}
                  placeholder="Victoria"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressZip">Postal Code</Label>
                <Input
                  id="addressZip"
                  value={settings.businessAddress?.zipCode || ''}
                  onChange={(e) => onSettingsChange({ 
                    businessAddress: { ...settings.businessAddress, zipCode: e.target.value } 
                  })}
                  placeholder="3058"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical SEO */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="text-foreground">Technical SEO</CardTitle>
          <CardDescription className="text-muted-foreground">
            Google verification and analytics integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="googleVerification">Google Site Verification</Label>
            <Input
              id="googleVerification"
              value={settings.googleSiteVerification || ''}
              onChange={(e) => onSettingsChange({ googleSiteVerification: e.target.value })}
              placeholder="google-site-verification=..."
            />
            <p className="text-xs text-muted-foreground">
              Get this code from Google Search Console to verify site ownership
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="googleAnalytics">Google Analytics ID</Label>
            <Input
              id="googleAnalytics"
              value={settings.googleAnalyticsId || ''}
              onChange={(e) => onSettingsChange({ googleAnalyticsId: e.target.value })}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs text-muted-foreground">
              Your Google Analytics 4 measurement ID
            </p>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-medium">Structured Data Controls</h4>
            <p className="text-sm text-muted-foreground">
              These controls enable rich snippets and advanced search features like star ratings, prices, and business info.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Enable Structured Data</Label>
                  <p className="text-xs text-muted-foreground">
                    Master control for all structured data features
                  </p>
                </div>
                <Switch
                  checked={settings.enableStructuredData !== false}
                  onCheckedChange={(checked) => onSettingsChange({ enableStructuredData: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Product Schema</Label>
                  <p className="text-xs text-muted-foreground">
                    Shows prices, ratings, and availability in search results
                  </p>
                </div>
                <Switch
                  checked={settings.enableProductSchema !== false}
                  onCheckedChange={(checked) => onSettingsChange({ enableProductSchema: checked })}
                  disabled={!settings.enableStructuredData}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Organization Schema</Label>
                  <p className="text-xs text-muted-foreground">
                    Displays business info and contact details in search results
                  </p>
                </div>
                <Switch
                  checked={settings.enableOrganizationSchema !== false}
                  onCheckedChange={(checked) => onSettingsChange({ enableOrganizationSchema: checked })}
                  disabled={!settings.enableStructuredData}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Website Schema</Label>
                  <p className="text-xs text-muted-foreground">
                    Enables site search box in Google results
                  </p>
                </div>
                <Switch
                  checked={settings.enableWebsiteSchema !== false}
                  onCheckedChange={(checked) => onSettingsChange({ enableWebsiteSchema: checked })}
                  disabled={!settings.enableStructuredData}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOSettings;
